import React, {
    Fragment,
    FunctionComponent,
    useEffect,
    useState,
} from 'react';
import {
    Col,
    Dropdown,
    DropdownButton,
    Row as BSRow,
    Table,
} from 'react-bootstrap';
import { ApplicationState } from '../../../redux/States';
import { ErrorDispatches } from '../../../redux/Dispatches';
import { ConnectedProps, connect } from 'react-redux';
import {
    Row,
    downloadDataAsPDFByPrintFromTable,
    downloadSheets,
    generateSheet,
} from '../../../utils';
import { AuthedLayout } from '../../../components';
import {
    InjuryGradeClassStatistic,
    UserInfo,
} from '../../../model';
import { useMonth, useSemGrade } from '../../../hook';
import apis from '../../../apis';
import { toast } from 'react-toastify';

const borderStyle = {
    color: { rgb: '000000' },
    style: 'thin',
} as const;

const fullBorderStyle = {
    top: borderStyle,
    bottom: borderStyle,
    left: borderStyle,
    right: borderStyle,
} as const;

const cellAlignStyle = {
    horizontal: 'center',
    vertical: 'center',
} as const;

const mapState = (
    state: ApplicationState
) => ({
    ...state.auth,
});

const mapDispatches = ErrorDispatches;

const connector = connect(
    mapState,
    mapDispatches
);

type Props =
    ConnectedProps<typeof connector>;

const injuryAccStCount: FunctionComponent<Props> = ({
    catchErrorForModal,
}) => {
    const {
        yearSem,
        element: semGradeElement,
    } = useSemGrade();

    const {
        selectedmonth,
        element: monthElement,
    } = useMonth({
        yearSem,
    });

    const [datas, setDatas] =
        useState<InjuryGradeClassStatistic[]>([]);

    const [userinfo, setUser] =
        useState<UserInfo>();

    useEffect(() => {
        if (
            yearSem &&
            selectedmonth
        ) {
            apis
                .getUserInfo()
                .then((u) => {
                    setUser(u);
                })
                .catch(
                    catchErrorForModal
                );

            toast
                .promise(
                    apis
                        .getInjuryAccStCount(
                            yearSem.year,
                            yearSem.sem,
                            selectedmonth.id
                        )
                        .then((s) => {
                            setDatas(s);
                        }),
                    {
                        pending:
                            '資料載入中......',
                        success:
                            '資料載入成功！',
                        error:
                            '資料載入失敗！請查看錯誤資訊。',
                    }
                )
                .catch(
                    catchErrorForModal
                );
        }
    }, [
        selectedmonth?.id,
        yearSem?.year,
        yearSem?.sem,
    ]);

    const gradeBase =
        datas.reduce(
            (
                grades,
                d
            ) => {
                return {
                    ...grades,
                    [d.grade]: [
                        ...(grades[
                            d.grade
                        ] || []),
                        d,
                    ],
                };
            },
            {} as Record<
                number,
                InjuryGradeClassStatistic[]
            >
        );

    let schoolcount = 0;
    let schoolfemalecount = 0;
    let schoolmalecount = 0;

    const contentRows: Row[] =
        Object.values(
            gradeBase
        ).flatMap(
            (
                gs,
                idx,
                allArray
            ) => {
                const classInjury = {
                    grade: 0,
                    classNo: 0,
                    totalCount: 0,
                    femaleCount: 0,
                    maleCount: 0,
                };

                const rows =
                    gs.flatMap(
                        (
                            g,
                            classIndex
                        ) => {
                            return TeeToRows(
                                g.grade,
                                g.className,
                                g.male?.count ??
                                0,
                                g.female?.count ??
                                0,
                                g.total?.count ??
                                0,
                                classIndex ===
                                    0
                                    ? {
                                        row:
                                            gs.length +
                                            1,
                                    }
                                    : undefined
                            );
                        }
                    );

                gs.forEach((item) => {
                    classInjury.totalCount +=
                        item.total
                            ?.count ??
                        0;

                    classInjury.femaleCount +=
                        item.female
                            ?.count ??
                        0;

                    classInjury.maleCount +=
                        item.male
                            ?.count ??
                        0;

                    schoolcount +=
                        item.total
                            ?.count ??
                        0;

                    schoolfemalecount +=
                        item.female
                            ?.count ??
                        0;

                    schoolmalecount +=
                        item.male
                            ?.count ??
                        0;
                });

                rows.push(
                    ...TeeToRows(
                        '',
                        '小計',
                        classInjury.maleCount,
                        classInjury.femaleCount,
                        classInjury.totalCount
                    )
                );

                if (
                    idx ===
                    allArray.length -
                    1
                ) {
                    const lastRow =
                        rows[
                        rows.length - 1
                        ];

                    lastRow.cells =
                        lastRow.cells.map(
                            (c) => {
                                const {
                                    style: {
                                        border = {},
                                    } = {},
                                } = c;

                                border.bottom =
                                    borderStyle;

                                c.style = {
                                    ...c.style,
                                    border,
                                };

                                return c;
                            }
                        );
                }

                return rows;
            }
        );

    const content: Row[] = [
        {
            cells: [
                {
                    value:
                        '年班/性別',
                    merge: {
                        column: 2,
                    },
                    style: {
                        border:
                            fullBorderStyle,
                    },
                },
                {
                    value: '男',
                    merge: {
                        column: 1,
                    },
                    style: {
                        border:
                            fullBorderStyle,
                    },
                },
                {
                    value: '女',
                    merge: {
                        column: 1,
                    },
                    style: {
                        border:
                            fullBorderStyle,
                    },
                },
                {
                    value: '小計',
                    merge: {
                        column: 1,
                    },
                    style: {
                        border:
                            fullBorderStyle,
                    },
                },
            ],
        },
        ...contentRows,
        ...TeeToRows(
            '全校合計',
            '',
            schoolmalecount,
            schoolfemalecount,
            schoolcount,
            {
                column: 2,
                row: 1,
            }
        ),
    ];

    const title =
        `${yearSem?.year ?? ''}學年第` +
        `${yearSem?.sem ?? ''}學期_` +
        `傷病人數統計(分月)`;

    const footer =
        '承辦人:　　　　　　　　　　　　　組長:　　　　　　　　　　　　　主任:　　　　　　　　　　　　　　校長:　　　　　　　　　　　　';

    const handleExcelDownload = () => {
        const workSheet =
            generateSheet(
                [
                    {
                        cells: [
                            {
                                value:
                                    title,
                                style: {
                                    alignment: {
                                        horizontal:
                                            'left',
                                    },
                                },
                            },
                        ],
                    },
                    ...content,
                    {
                        cells: [
                            {
                                value:
                                    footer,
                                style: {
                                    alignment: {
                                        horizontal:
                                            'left',
                                    },
                                },
                            },
                        ],
                    },
                ],
                {
                    alignment:
                        cellAlignStyle,
                }
            );

        downloadSheets(
            title,
            userinfo?.exportFileType,
            {
                sheet: workSheet,
                name: 'st',
            }
        );
    };

    const handlePrint = () => {
        downloadDataAsPDFByPrintFromTable(
            title,
            footer
        );
    };

    return (
        <AuthedLayout>
            <BSRow className="justify-content-between">
                <Col>
                    <BSRow>
                        <Col
                            xs={3}
                            className="mr-3"
                        >
                            {
                                semGradeElement
                            }
                        </Col>

                        <Col
                            xs={3}
                            className="mr-3"
                        >
                            {
                                monthElement
                            }
                        </Col>
                    </BSRow>
                </Col>

                <div>
                    <DropdownButton
                        id="injury-acc-st-count-export"
                        title="匯出"
                        variant="success"
                        disabled={
                            !datas.length
                        }
                    >
                        <Dropdown.Item
                            onClick={
                                handleExcelDownload
                            }
                        >
                            Excel 下載
                        </Dropdown.Item>

                        <Dropdown.Item
                            onClick={
                                handlePrint
                            }
                        >
                            列印
                        </Dropdown.Item>
                    </DropdownButton>
                </div>
            </BSRow>

            <hr />

            <BSRow>
                <Table
                    id="print-table"
                    striped
                    bordered
                    className="text-center"
                    style={{
                        tableLayout:
                            'fixed',
                    }}
                >
                    <colgroup>
                        <col
                            style={{
                                width:
                                    '10%',
                            }}
                        />

                        <col
                            style={{
                                width:
                                    '20%',
                            }}
                        />

                        <col
                            style={{
                                width:
                                    '20%',
                            }}
                        />

                        <col
                            style={{
                                width:
                                    '20%',
                            }}
                        />

                        <col
                            style={{
                                width:
                                    '20%',
                            }}
                        />
                    </colgroup>

                    <tbody>
                        {content.map(
                            (
                                r,
                                i
                            ) => {
                                const cells =
                                    r.cells.map(
                                        (
                                            c,
                                            j
                                        ) =>
                                            c.value !==
                                                undefined ? (
                                                <td
                                                    colSpan={
                                                        c
                                                            .merge
                                                            ?.column
                                                    }
                                                    rowSpan={
                                                        c
                                                            .merge
                                                            ?.row
                                                    }
                                                    key={`${i}-${j}`}
                                                >
                                                    {
                                                        c.value
                                                    }
                                                </td>
                                            ) : (
                                                <Fragment
                                                    key={`${i}-${j}`}
                                                />
                                            )
                                    );

                                return (
                                    <tr
                                        key={
                                            i
                                        }
                                    >
                                        {
                                            cells
                                        }
                                    </tr>
                                );
                            }
                        )}
                    </tbody>
                </Table>
            </BSRow>
        </AuthedLayout>
    );
};

function TeeToRows(
    grade: number | string,
    classNo: number | string,
    maleCount: number,
    femaleCount: number,
    totalCount: number,
    gradeMerge?: {
        row?: number;
        column?: number;
    }
): Row[] {
    const gradeNo = [
        {
            value: gradeMerge
                ? grade
                : undefined,
            merge: gradeMerge,
            style: {
                border:
                    fullBorderStyle,
            },
        },
    ];

    if (
        !gradeMerge?.column
    ) {
        gradeNo.push({
            value: classNo,
            merge: {
                row: 1,
            },
            style: {
                border:
                    fullBorderStyle,
            },
        });
    }

    return [
        {
            cells: [
                ...gradeNo,
                {
                    value:
                        maleCount ??
                        0,
                    style: {
                        border: {
                            left:
                                borderStyle,
                        },
                    },
                },
                {
                    value:
                        femaleCount ??
                        0,
                    style: {
                        border: {
                            left:
                                borderStyle,
                        },
                    },
                },
                {
                    value:
                        totalCount ??
                        0,
                    style: {
                        border: {
                            left:
                                borderStyle,
                        },
                    },
                },
            ],
        },
    ];
}

export const InjuryAccStCount = connector(injuryAccStCount);